/**
 * Notification field parameters for API calls
 * This file defines the fields that are actually used in the frontend
 * to limit the data returned from the notifications API
 */

// Core notification fields that are always needed
export const NOTIFICATION_CORE_FIELDS = [
  'id',
  'notification_type',
  'content',
  'created',
  'is_read',
  'read_at',
  'url',
  'notification_source',
  'is_single_notification',
  'is_bulk_notification',
  'is_property_notification',
  'is_system_notification',
  'notification_type_category',
  'related_objects',
];

// Certification fields that are actually used in the frontend
export const CERTIFICATION_FIELDS = [
  'certifications.id',
  'certifications.display_name',
  'certifications.effective_date',
  'certifications.deadline',
  'certifications.certification_config.name',
];

// Property fields that are actually used in the frontend
export const PROPERTY_FIELDS = [
  'certifications.property.slug',
  'certifications.property.name',
  'certifications.property.enable_renter_notifications',
  'properties.id',
  'properties.name',
];

// Household fields that are actually used in the frontend
export const HOUSEHOLD_FIELDS = [
  'certifications.household_name',
  'certifications.household.name',
];

/**
 * Generates the query parameters for the notifications API call
 * @returns {string} URL query parameters for limiting returned fields
 * @throws {Error} If required fields are missing or invalid
 */
export const getNotificationQueryParams = () => {
  try {
    // Validate that all required field arrays exist
    const requiredFieldArrays = [
      NOTIFICATION_CORE_FIELDS,
      CERTIFICATION_FIELDS,
      PROPERTY_FIELDS,
      HOUSEHOLD_FIELDS,
    ];

    for (const fieldArray of requiredFieldArrays) {
      if (!Array.isArray(fieldArray)) {
        throw new Error('Field arrays must be arrays');
      }
      if (fieldArray.length === 0) {
        throw new Error('Field arrays cannot be empty');
      }
    }

    // Combine all fields
    const allFields = [
      ...NOTIFICATION_CORE_FIELDS,
      ...CERTIFICATION_FIELDS,
      ...PROPERTY_FIELDS,
      ...HOUSEHOLD_FIELDS,
    ];

    // Remove duplicates
    const uniqueFields = [...new Set(allFields)];

    // Validate field format (should not contain spaces or special characters)
    const invalidFields = uniqueFields.filter(
      (field) => !/^[a-zA-Z0-9._]+$/.test(field)
    );

    if (invalidFields.length > 0) {
      throw new Error(
        `Invalid field format detected: ${invalidFields.join(', ')}`
      );
    }

    // Build query parameters
    const expandParams =
      'certifications,certifications.property,certifications.household,certifications.certification_config,properties';
    const fieldsParams = uniqueFields.join(',');

    return `expand=${expandParams}&fields=${fieldsParams}`;
  } catch (error) {
    console.error('Error generating notification query parameters:', error);
    // Return a safe fallback that includes essential fields
    return 'expand=certifications,certifications.property,certifications.household,certifications.certification_config,properties&fields=id,notification_type,content,created,is_read,url,notification_source,is_bulk_notification,is_single_notification,is_property_notification,is_system_notification,notification_type_category,related_objects,certifications.household_name,certifications.household.name';
  }
};

/**
 * Validates that all required notification fields are present
 * @param {Object} notification - The notification object to validate
 * @returns {boolean} True if all required fields are present
 */
export const validateNotificationFields = (notification) => {
  if (!notification || typeof notification !== 'object') {
    return false;
  }

  const requiredFields = [
    'id',
    'notification_type',
    'content',
    'created',
    'is_read',
    'url',
    'notification_source',
  ];

  return requiredFields.every(
    (field) =>
      Object.prototype.hasOwnProperty.call(notification, field) &&
      notification[field] !== undefined
  );
};

/**
 * Gets the total number of fields being requested
 * @returns {number} Total number of fields
 */
export const getTotalFieldCount = () => {
  const allFields = [
    ...NOTIFICATION_CORE_FIELDS,
    ...CERTIFICATION_FIELDS,
    ...PROPERTY_FIELDS,
    ...HOUSEHOLD_FIELDS,
  ];
  return [...new Set(allFields)].length;
};

/**
 * Notification source types
 */
export const NOTIFICATION_SOURCE_TYPES = {
  FILE_OWNER: 'file_owner',
  WATCHED: 'watched',
  PROPERTY: 'property',
  RENTER: 'renter',
};

export const getNotificationSourceValue = (notification) => {
  return notification?.notification_source || notification?.source;
};

/**
 * Determines if a notification is for a watched certification
 * @param {Object} notification - The notification object
 * @returns {boolean} True if the notification is for a watched certification
 */
export const isWatchedNotification = (notification) => {
  return (
    getNotificationSourceValue(notification) ===
    NOTIFICATION_SOURCE_TYPES.WATCHED
  );
};

/**
 * Determines if a notification is for a file owner
 * @param {Object} notification - The notification object
 * @returns {boolean} True if the notification is for a file owner
 */
export const isFileOwnerNotification = (notification) => {
  return (
    getNotificationSourceValue(notification) ===
    NOTIFICATION_SOURCE_TYPES.FILE_OWNER
  );
};

/**
 * Determines if a notification is for a property
 * @param {Object} notification - The notification object
 * @returns {boolean} True if the notification is for a property
 */
export const isPropertyNotification = (notification) => {
  return (
    getNotificationSourceValue(notification) ===
    NOTIFICATION_SOURCE_TYPES.PROPERTY
  );
};

/**
 * Gets the display name for a notification source
 * @param {string} notificationSource - The notification source value
 * @returns {string} The display name for the notification source
 */
export const getNotificationSourceDisplayName = (notificationSource) => {
  switch (notificationSource) {
    case NOTIFICATION_SOURCE_TYPES.FILE_OWNER:
      return 'File Owner';
    case NOTIFICATION_SOURCE_TYPES.WATCHED:
      return 'Watching';
    case NOTIFICATION_SOURCE_TYPES.PROPERTY:
      return 'Property';
    case NOTIFICATION_SOURCE_TYPES.RENTER:
      return 'Renter';
    default:
      return 'Unknown';
  }
};
